import React, {
  useState,
  ReactElement,
  useMemo,
  useCallback,
  useRef,
  useEffect,
  isValidElement,
  cloneElement,
} from 'react';
import {
  MedicationSearchResult,
  ParsedArticleSearchResult,
  SearchContentType,
  SearchResultData,
} from '@novaheal/types';
import { VideoSearchResult } from '@novaheal/types/lib/search/videoSearch';
import { MedicationCard } from 'components/common/MedicationCard';
import { HStack, SimpleGrid, VStack, Text, Spinner, Box } from '@chakra-ui/react';
import type { NovaIconProps } from 'components/ui/novaIcon';
import { useBreakpoint } from 'hooks/utils/layout/useBreakpoint';
import placeholderImg from 'assets/common/placeholder.png';
import { ImageSearchResult } from '@novaheal/types/lib/search/imageSearch';
import {
  isArticleSearchResult,
  isImageSearchResult,
  isMedicationSearchResult,
  isVideoSearchResult,
} from 'types/SearchResultWithParsedArticle';
import { parseSearchArticles } from '@novaheal/parsers';
import {
  type AlgoliaPageResult,
  useInfiniteAlgoliaContent,
} from 'hooks/api/backend/search/useInfiniteAlgoliaContent';
import { useModalService } from 'hooks/useModalService';
import { useFeatureFlagsContext } from 'hooks/utils/useFeatureFlagContext';
import { TabContentCategory } from 'types/tab';
import { Breakpoints } from 'types/breakpoints';
import { SessionStorageKey } from 'hooks/useSessionStorage';
import { ModalVariant } from 'types/Modal';
import { shouldShowSearchFilterTrigger } from './searchFilterCategories';
import { SearchFilterTrigger } from './SearchFilterTrigger';
import { SearchResult } from './SearchResult';
import { SearchResultText } from './SearchResultText';
import { VideoCard } from './VideoCard';
import { ImageCard } from './ImageCard';

type SearchResultWithParsedArticle =
  | MedicationSearchResult
  | ParsedArticleSearchResult
  | VideoSearchResult
  | ImageSearchResult;

const handleClick = (id: string): void => {
  sessionStorage.setItem(SessionStorageKey.LAST_SEARCH_CLICKED_ID, id);
};

const renderBasedOnType = (result: SearchResultWithParsedArticle): ReactElement | null => {
  if (isArticleSearchResult(result)) {
    return (
      <SearchResult
        key={result.objectID}
        id={result.objectID}
        title={result.fields.title}
        imageUrl={result.fields.coverImage?.url || placeholderImg}
        subtitle={result.fields.category.name}
        hasVideo={result.fields.hasVideo}
        relevantYearNames={result.fields.relevantYears}
        description={result.highlightData?.value}
        csId={result.highlightData?.csId}
        handleClick={() => handleClick(result.objectID)}
      />
    );
  }
  if (isMedicationSearchResult(result)) {
    return (
      <MedicationCard
        key={result.objectID}
        id={result.objectID}
        name={result.fields.name}
        trademark={result.fields.trademark}
        indications={result.fields.indications}
        handleClick={() => handleClick(result.objectID)}
      />
    );
  }
  if (isVideoSearchResult(result)) {
    return (
      <VideoCard
        id={result.objectID}
        key={result.objectID}
        title={result.fields.title}
        thumbnailUrl={result.fields.thumbnailUrl}
        description={result.fields.description}
        videoUrl={result.fields.videoUrl}
      />
    );
  }
  if (isImageSearchResult(result)) {
    return (
      <ImageCard
        id={result.objectID}
        key={result.objectID}
        title={result.fields.title}
        imageUrl={result.fields.imageUrl}
        description={result.fields.description}
      />
    );
  }

  return null;
};

type Props = {
  query?: string;
  numOfColumns?: number;
  icon?: ReactElement;
  title: string;
  footerElement?: ReactElement | null;
  isActive?: boolean;
  categories?: TabContentCategory[];
};

const mapCategoryToContentType = (categories: TabContentCategory[]): SearchContentType[] => {
  return categories.flatMap((cat) => {
    switch (cat) {
      case TabContentCategory.ARTICLE:
        return [SearchContentType.ARTICLE];
      case TabContentCategory.MEDIA:
        return [SearchContentType.VIDEO, SearchContentType.IMAGE];
      case TabContentCategory.MEDICATION:
        return [SearchContentType.MEDICATION];
      default:
        return [];
    }
  });
};

export const SearchResultList = ({
  query,
  numOfColumns = 1,
  icon,
  title,
  footerElement,
  isActive = false,
  categories = [],
}: Props): ReactElement => {
  const [selectedCeTags, setSelectedCeTags] = useState<string[]>([]);
  const [selectedKbTags, setSelectedKbTags] = useState<string[]>([]);
  const modalService = useModalService();
  const featureFlags = useFeatureFlagsContext();
  const isSearchFilterEnabled = featureFlags.isEnabled('isSearchFilterEnabled');

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteAlgoliaContent({
      contentType: mapCategoryToContentType(categories),
      query: query || '',
      disabled: !isActive,
      selectedCeTags,
      selectedKbTags,
    });
  const isDesktop = useBreakpoint(Breakpoints.DESKTOP);

  const loaderRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledRef = useRef(false);
  const scrollItemId = sessionStorage.getItem(SessionStorageKey.LAST_SEARCH_CLICKED_ID);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Observe the loader to trigger loading more
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          handleLoadMore();
        }
      },
      { rootMargin: '0px 0px 20px 0px' } // load before user reaches the bottom
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    const scrollToAnchorLink = (): void => {
      if (hasScrolledRef.current) return;
      if (!scrollItemId) return;
      hasScrolledRef.current = true;
      const element = document.getElementById(scrollItemId);
      if (element) {
        const headerOffset = isDesktop ? 120 : 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      } else {
        sessionStorage.removeItem(SessionStorageKey.LAST_SEARCH_CLICKED_ID);
      }
    };
    if (!isLoading && scrollItemId) {
      scrollToAnchorLink();
    }
    return () => {
      observer.disconnect();
    };
  }, [handleLoadMore, isDesktop, isLoading, scrollItemId]);

  const dataPages = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page: AlgoliaPageResult) => page.hits);
  }, [data]);

  const parsedResults = useMemo(() => {
    const results: SearchResultWithParsedArticle[] = [];

    dataPages.forEach((item: SearchResultData) => {
      if (item.contentType === SearchContentType.ARTICLE) {
        const parsed = parseSearchArticles([item]);
        if (parsed && parsed.length > 0) {
          results.push(parsed[0]);
        }
      } else {
        results.push(item as SearchResultWithParsedArticle);
      }
    });

    return results;
  }, [dataPages]);

  const filteredTotalCount = data?.pages?.[0]?.nbHits ?? 0;

  if (isLoading) {
    return (
      <VStack
        bg="background.primary"
        p={{ base: '10px 28px', md: '20px' }}
        borderRadius={{ md: '2xl' }}
        align="center"
        justify="center"
        minH="200px"
      >
        <Spinner size="lg" color="text.primary" />
      </VStack>
    );
  }

  return (
    <VStack
      bg="background.primary"
      mt="l"
      p={{ base: '10px 28px', md: '20px' }}
      borderRadius={{ md: '2xl' }}
      align="left"
      gap="15px"
    >
      <HStack gap="5px" alignItems="center" justifyContent="space-between">
        <HStack gap="5px" alignItems="center">
          {icon &&
            isValidElement(icon) &&
            cloneElement(icon, {
              color: 'icon.primary',
              size: 's',
            } as Partial<NovaIconProps>)}
          <Text textStyle="h4.regular" color="text.secondary">
            {title}
          </Text>
        </HStack>
        <Box>
          {isSearchFilterEnabled &&
          shouldShowSearchFilterTrigger(mapCategoryToContentType(categories)) ? (
            <SearchFilterTrigger
              activeFilterCount={selectedCeTags.length + selectedKbTags.length}
              onClick={() =>
                modalService.show(ModalVariant.SEARCH_FILTER, {
                  selectedCeTags,
                  selectedKbTags,
                  setSelectedCeTags,
                  setSelectedKbTags,
                })
              }
            />
          ) : null}
        </Box>
      </HStack>

      <SearchResultText count={filteredTotalCount} searchQuery={query} />

      <SimpleGrid
        columns={numOfColumns}
        rowGap={
          numOfColumns > 1
            ? {
                base: 4,
                md: '36px',
              }
            : 0
        }
        columnGap={
          numOfColumns > 1
            ? {
                base: 4,
                md: '20px',
              }
            : 0
        }
        width="100%"
      >
        {parsedResults.map((result) => (
          <React.Fragment key={result.objectID}>{renderBasedOnType(result)}</React.Fragment>
        ))}
      </SimpleGrid>

      {/* Loader trigger */}
      {hasNextPage && (
        <Box ref={loaderRef} width="100%" textAlign="center" py="m">
          {isFetchingNextPage && <Spinner />}
        </Box>
      )}

      {footerElement && footerElement}
    </VStack>
  );
};
